import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Icon from '../components/common/Icon';
import { HeroBackgroundStrokes } from '../components/common/SyncratikLogo';
import newsData from '../data/news.json';
import ReactMarkdown from 'react-markdown';


const ArticleDetailPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    // Create a sorted copy to ensure consistent prev/next navigation
    const sortedNewsData = [...newsData].sort((a, b) => new Date(b.datePublished) - new Date(a.datePublished));
    const articleData = sortedNewsData.find(news => news.slug === slug);
    const [headings, setHeadings] = useState([]);
    const [activeHeading, setActiveHeading] = useState('');

    // Extract headings from markdown content
    useEffect(() => {
        if (!articleData) return;

        const headingRegex = /^### (.+)$/gm;
        const matches = [...articleData.content.matchAll(headingRegex)];
        const extractedHeadings = matches.map(match => ({
            text: match[1],
            id: match[1].toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        }));
        setHeadings(extractedHeadings);
    }, [articleData]);

    // Track active section on scroll
    useEffect(() => {
        const handleScroll = () => {
            const headingElements = headings.map(h => document.getElementById(h.id)).filter(Boolean);

            for (let i = headingElements.length - 1; i >= 0; i--) {
                const element = headingElements[i];
                const rect = element.getBoundingClientRect();
                if (rect.top <= 150) {
                    setActiveHeading(element.id);
                    return;
                }
            }
            if (headingElements.length > 0) {
                setActiveHeading(headingElements[0].id);
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
        return () => window.removeEventListener('scroll', handleScroll);
    }, [headings]);

    if (!articleData) return <div className="py-24 text-center text-red-300">Article not found</div>;

    // Smooth scroll to section
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth'
            });
        }
    };

    // formate the date
    const formattedPublishedDate = new Date(articleData.datePublished).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
    })

    const formatReadTime = (minutes) => {
        if (!minutes) return null;
        return `${minutes} ${minutes === 1 ? "min" : "mins"}`;
    };

    // Helper function to extract plain text from React children (handles inline formatting)
    const extractTextFromChildren = (children) => {
        if (typeof children === 'string') return children;
        if (typeof children === 'number') return String(children);
        if (Array.isArray(children)) {
            return children.map(extractTextFromChildren).join('');
        }
        if (children?.props?.children) {
            return extractTextFromChildren(children.props.children);
        }
        return '';
    };

    // News content styling
    const customRenderers = {
        p: ({ children }) => (
            <p className="text-paragraph text-left">
                {children}
            </p>
        ),
        h3: ({ children }) => {
            const text = extractTextFromChildren(children);
            const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            return (
                <h3 id={id} className="text-h3 font-semibold mt-8 pt-4 mb-4 pb-2 border-b border-gray-30">
                    {children}
                </h3>
            );
        },
        h4: ({ children }) => (
            <h4 className="text-paragraph font-medium pt-3 pb-2 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-1/2 after:h-px after:bg-gray-30">
                {children}
            </h4>
        ),
        ul: ({ children }) => (
            <ul className="list-disc ml-6 space-y-2 my-4">
                {children}
            </ul>
        ),
        ol: ({ children }) => (
            <ol className="list-decimal ml-6 space-y-2 my-4">
                {children}
            </ol>
        ),
        li: ({ children }) => (
            <li className="text-paragraph list-item">
                {children}
            </li>
        ),
        a: ({ href, children }) => (
            <a
                href={href}
                className="text-purple-60 hover:text-purple-50 transition-colors duration-200"
                target="_blank" // Opens link in a new tab
                rel="noopener noreferrer" // Security recommendation for target="_blank"
            >
                {children}
            </a>
        )
        // Style other elements if necessary
    };

    return (
        <div className="bg-gray-10 min-h-screen relative pt-16 pb-60">
            {/* Background with logo strokes */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <HeroBackgroundStrokes
                    className="absolute inset-0 w-full"
                    style={{
                        height: '100%',
                        backgroundImage: 'url(/assets/logo-strokes.png)',
                        backgroundRepeat: 'repeat-y',
                        backgroundSize: '100% 2216px',
                        backgroundPosition: 'center top'
                    }}
                />
            </div>

            <div className="container-content relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-8">
                    {/* Sidebar - Back Button + Table of Contents - Desktop Only */}
                    <aside className="hidden lg:block">
                        <nav className="sticky top-24">
                            {/* Back Button */}
                            <button
                                onClick={() => navigate(-1)}
                                className="text-paragraph text-gray-50 hover:text-gray-95 mb-4 flex items-center"
                            >
                                <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="w-3 h-3 mr-2" />
                                Back
                            </button>

                            {/* Table of Contents */}
                            {headings.length > 0 && (
                                <>
                                    <h3 className="text-small font-semibold text-gray-80 mb-4 uppercase tracking-wide">
                                        On This Page
                                    </h3>
                                    <ul className="space-y-3">
                                        {headings.map((heading) => (
                                            <li key={heading.id}>
                                                <button
                                                    onClick={() => scrollToSection(heading.id)}
                                                    className={`text-left text-small transition-colors duration-200 hover:text-purple-60 ${activeHeading === heading.id
                                                        ? 'text-purple-60 font-medium border-l-2 border-purple-60 pl-3'
                                                        : 'text-gray-60 pl-3'
                                                        }`}
                                                >
                                                    {heading.text}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </nav>
                    </aside>

                    <div className="lg:col-span-1">
                        <div className="max-w-3xl mx-auto">
                            {/* Back Button - Mobile Only */}
                            <button
                                onClick={() => navigate(-1)}
                                className="lg:hidden text-paragraph text-gray-50 hover:text-gray-95 mb-4 flex items-center"
                            >
                                <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="w-3 h-3 mr-2" />
                                Back
                            </button>

                            {/* Article Header - Left Aligned */}
                            <div className="text-left mb-10">
                                <h1 className="text-h2 sm:text-h1 text-gray-95 mb-3">{articleData.title}</h1>

                                {/* Single row metadata: Author/Editor on left, Date/Duration on right */}
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 text-gray-50">
                                    {/* Left side: Author and Editor */}
                                    <div className="flex flex-col">
                                        {articleData.author && (
                                            <p className="text-gray-80 text-small">
                                                Written by {articleData.author}
                                            </p>
                                        )}
                                        {articleData.editor && (
                                            <p className="text-gray-70 text-small">
                                                Edited by {articleData.editor}
                                            </p>
                                        )}
                                    </div>

                                    {/* Right side: Date and Duration */}
                                    <div className="flex flex-col sm:items-end">
                                        <p className="text-small">{formattedPublishedDate}</p>
                                        {articleData.estimatedReadMinutes && (
                                            <p className="text-small">
                                                {formatReadTime(articleData.estimatedReadMinutes)} read
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {articleData.imageUrl && (
                                <figure className="mb-10 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/50 shadow">
                                    <img
                                        src={articleData.imageUrl.startsWith('http') ? articleData.imageUrl : `/news/${articleData.imageUrl}`}
                                        alt={articleData.title}
                                        className="w-full h-64 md:h-[24rem] object-cover"
                                        loading="lazy"
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                </figure>
                            )}

                            {/* content */}
                            <article className="px-2 max-w-3xl mx-auto text-gray-95 leading-7 space-y-4 backdrop-blur-sm bg-gray-10/5">
                                <ReactMarkdown components={customRenderers}>
                                    {articleData.content}
                                </ReactMarkdown>
                            </article>

                            {/* Related Articles Navigation */}
                            <div className="mt-16 pt-8 border-t border-gray-30 max-w-3xl mx-auto">
                                <h3 className="text-h3 text-gray-95 mb-6">Continue Reading</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Previous Article */}
                                    {(() => {
                                        const currentIndex = sortedNewsData.findIndex(news => news.slug === slug);
                                        const prevArticle = currentIndex < sortedNewsData.length - 1 ? sortedNewsData[currentIndex + 1] : null;

                                        return prevArticle ? (
                                            <Link
                                                to={`/news/${prevArticle.slug}`}
                                                className="group py-4 transition-all duration-300 backdrop-blur-sm bg-gray-10/5"
                                            >
                                                <div className="flex items-center gap-2 text-gray-50 text-small mb-3">
                                                    <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="w-3 h-3" />
                                                    <span>Previous</span>
                                                </div>
                                                <h4 className="text-subheading font-semibold text-gray-80 group-hover:text-purple-60 transition-colors">
                                                    {prevArticle.title}
                                                </h4>
                                                <p className="text-paragraph text-gray-60">
                                                    {new Date(prevArticle.datePublished).toLocaleDateString("en-US", {
                                                        year: "numeric",
                                                        month: "long",
                                                        day: "numeric"
                                                    })}
                                                </p>
                                            </Link>
                                        ) : null;
                                    })()}

                                    {/* Next Article */}
                                    {(() => {
                                        const currentIndex = sortedNewsData.findIndex(news => news.slug === slug);
                                        const nextArticle = currentIndex > 0 ? sortedNewsData[currentIndex - 1] : null;

                                        return nextArticle ? (
                                            <Link
                                                to={`/news/${nextArticle.slug}`}
                                                className="group py-4 transition-all duration-300 backdrop-blur-sm bg-gray-10/5"
                                            >
                                                <div className="flex items-center justify-end gap-2 text-gray-50 text-small mb-3">
                                                    <span>Next</span>
                                                    <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="w-3 h-3" />
                                                </div>
                                                <h4 className="text-subheading font-semibold text-gray-80 group-hover:text-purple-60 transition-colors text-right">
                                                    {nextArticle.title}
                                                </h4>
                                                <p className="text-paragraph text-gray-60 text-right">
                                                    {new Date(nextArticle.datePublished).toLocaleDateString("en-US", {
                                                        year: "numeric",
                                                        month: "long",
                                                        day: "numeric"
                                                    })}
                                                </p>
                                            </Link>
                                        ) : null;
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArticleDetailPage;